import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Building2, Mail, Phone, Users, Loader2, Check, ArrowLeft, MapPin } from "lucide-react";
import { toast } from "sonner";

import { useT } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// B2B tiers — lead-gen only, prices are "from" estimates. Real quote comes
// from Kaily after a discovery call. Keep tier slugs short — they get stored
// as `tier` on the inquiry and shown in Studio.
const TIERS = [
    {
        slug: "5pack",
        outlets: "5",
        outlets_label_en: "5 outlets",
        outlets_label_cn: "5 个门店",
        price_from: "RM 880",
        price_period_en: "/month",
        price_period_cn: "/月",
        en_features: [
            "5 × 30-day AI Content Kit (one per outlet)",
            "Brand-locked palette across all locations",
            "Single intake — answer once, applies to all",
            "Quarterly catch-up call with Kaily",
            "Priority WhatsApp support (≤6 hr response)",
        ],
        cn_features: [
            "5 × 30 天 AI 内容包（每店一份）",
            "全门店统一品牌色调",
            "一份 intake — 填一次套用全店",
            "每季度与 Kaily 复盘电话",
            "优先 WhatsApp 支持（≤6 小时回复）",
        ],
    },
    {
        slug: "10pack",
        outlets: "10",
        outlets_label_en: "10 outlets",
        outlets_label_cn: "10 个门店",
        price_from: "RM 1,580",
        price_period_en: "/month",
        price_period_cn: "/月",
        recommended: true,
        en_features: [
            "Everything in 5-Outlet Pack",
            "10 × 30-day kits + 2 free re-rolls per location",
            "Centralised approval dashboard for HQ marketing",
            "Monthly performance report (reach, saves, top posts)",
            "Dedicated Slack/WhatsApp group — direct line to Kaily",
        ],
        cn_features: [
            "包含 5 店套餐所有内容",
            "10 × 30 天内容包 + 每店 2 次免费修改",
            "总部市场部集中审批面板",
            "每月数据报告（触达、收藏、爆款）",
            "专属 Slack/WhatsApp 群 — 直接对接 Kaily",
        ],
    },
    {
        slug: "custom",
        outlets: "20+",
        outlets_label_en: "20+ outlets / custom",
        outlets_label_cn: "20+ 门店 / 定制",
        price_from: "Quote",
        price_period_en: "by call",
        price_period_cn: "电话报价",
        en_features: [
            "Built around your brand's existing workflow",
            "API access — sync with your in-house CMS",
            "White-labelled Publer / scheduling setup",
            "Annual retainer with locked-in rates",
            "On-location shoot days bundled (Klang Valley)",
        ],
        cn_features: [
            "深度定制 — 适配你现有工作流",
            "API 接入 — 与你 CMS 联动",
            "白标 Publer / 排期工具",
            "年付锁价协议",
            "可加配上门拍摄日（巴生谷）",
        ],
    },
];

function TierCard({ tier, lang, onSelect, isSelected }) {
    const features = lang === "cn" ? tier.cn_features : tier.en_features;
    const outletsLabel = lang === "cn" ? tier.outlets_label_cn : tier.outlets_label_en;
    const period = lang === "cn" ? tier.price_period_cn : tier.price_period_en;
    const fromLabel = lang === "cn" ? "起价" : "From";
    return (
        <button
            type="button"
            onClick={() => onSelect(tier.slug)}
            data-testid={`b2b-tier-${tier.slug}`}
            className={`relative text-left rounded-3xl p-7 sm:p-8 border-2 transition-all duration-300 ${isSelected
                ? "border-cafe-olive bg-cafe-olive/8 shadow-lg"
                : "border-cafe-olive/20 bg-cafe-cream hover:border-cafe-olive/50 hover:-translate-y-1"
                }`}
        >
            {tier.recommended && (
                <span className="absolute -top-3 left-7 px-3 py-1 rounded-full bg-cafe-terracotta text-cafe-cream text-[10px] uppercase tracking-[0.18em] font-bold">
                    {lang === "cn" ? "最受欢迎" : "Most popular"}
                </span>
            )}
            <div className="flex items-center gap-2 text-cafe-olive font-bold text-[11px] uppercase tracking-[0.18em] mb-3">
                <Building2 className="h-3.5 w-3.5" />
                {outletsLabel}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-cafe-mocha mb-1">{fromLabel}</p>
            <p className="font-display text-4xl sm:text-5xl text-cafe-espresso leading-none mb-1">{tier.price_from}</p>
            <p className="text-xs text-cafe-mocha mb-5">{period}</p>
            <ul className="space-y-2.5 mb-5">
                {features.map((f) => (
                    <li key={f} className="flex gap-2.5 text-[13px] sm:text-sm text-cafe-espresso leading-snug">
                        <Check className="h-4 w-4 shrink-0 mt-0.5 text-cafe-olive" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>
            <div className={`text-center py-2.5 rounded-full text-xs font-semibold uppercase tracking-[0.12em] ${isSelected
                ? "bg-cafe-olive text-cafe-cream"
                : "bg-white text-cafe-olive border border-cafe-olive/30"
                }`}>
                {isSelected ? (lang === "cn" ? "已选" : "Selected") : (lang === "cn" ? "选择此套餐" : "Pick this tier")}
            </div>
        </button>
    );
}

export default function B2B() {
    const { lang, t: _t } = useT();
    void _t;
    const [selectedTier, setSelectedTier] = useState("10pack");
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        locations: "",
        monthly_budget: "",
        notes: "",
    });

    const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (submitting || submitted) return;
        const locations = parseInt(form.locations, 10);
        if (!locations || locations < 2) {
            toast.error(lang === "cn" ? "至少 2 个门店" : "Minimum 2 outlets");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/b2b/inquiry`, {
                ...form,
                locations,
                tier: selectedTier,
                source: "b2b_page",
            });
            setSubmitted(true);
            toast.success(lang === "cn" ? "收到!Kaily 会 24 小时内联系你" : "Got it — Kaily will reply within 24h");
        } catch (err) {
            const detail = err?.response?.data?.detail;
            toast.error(typeof detail === "string" ? detail : (lang === "cn" ? "提交失败,请重试" : "Submission failed, please retry"));
        } finally {
            setSubmitting(false);
        }
    };

    const homePath = lang === "cn" ? "/cn" : "/";

    return (
        <div className="min-h-screen bg-cafe-paper" data-testid="b2b-page">
            {/* Top nav */}
            <header className="border-b border-cafe-olive/15 bg-cafe-cream/80 backdrop-blur sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 py-4 flex items-center justify-between">
                    <Link
                        to={homePath}
                        className="flex items-center gap-2 text-cafe-espresso hover:text-cafe-olive transition-colors"
                        data-testid="b2b-back-home"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span className="font-display text-lg">slowcafe.kaily</span>
                    </Link>
                    <Link
                        to={lang === "cn" ? "/" : "/cn"}
                        className="text-cafe-olive text-[11px] uppercase tracking-[0.2em] font-bold hover:underline"
                    >
                        {lang === "cn" ? "EN" : "中文"}
                    </Link>
                </div>
            </header>

            {/* Hero */}
            <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-12">
                <p className="text-cafe-olive font-bold text-[10px] uppercase tracking-[0.28em] mb-3">
                    {lang === "cn" ? "B2B · 连锁餐饮 / 多门店品牌" : "B2B · multi-location & chain restaurants"}
                </p>
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-cafe-espresso leading-[1.05] mb-5 max-w-3xl">
                    {lang === "cn" ? (
                        <>一套体系,管<em className="italic text-cafe-terracotta">所有门店</em>的 IG 内容。</>
                    ) : (
                        <>One system. <em className="italic text-cafe-terracotta">Every outlet's</em> Instagram, sorted.</>
                    )}
                </h1>
                <p className="text-cafe-mocha text-base sm:text-lg max-w-2xl leading-relaxed">
                    {lang === "cn"
                        ? "从 5 个门店到 50 个 — slowcafe.kaily 给你一份统一的品牌视觉手册和每月 AI 内容包,让连锁经理不再每天追门店要照片。"
                        : "From 5 outlets to 50 — slowcafe.kaily delivers one brand-locked visual system and monthly AI content packs, so your chain managers stop chasing each outlet for photos."}
                </p>
            </section>

            {/* Pricing tiers */}
            <section className="max-w-6xl mx-auto px-5 sm:px-8 pb-12 sm:pb-16">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6">
                    {TIERS.map((tier) => (
                        <TierCard
                            key={tier.slug}
                            tier={tier}
                            lang={lang}
                            isSelected={selectedTier === tier.slug}
                            onSelect={setSelectedTier}
                        />
                    ))}
                </div>
                <p className="text-cafe-mocha text-xs sm:text-sm italic text-center mt-6 max-w-2xl mx-auto">
                    {lang === "cn"
                        ? "所有套餐均含 30 天内容输出 + 品牌色锁定 + Publer 自动发布对接(可选)。具体报价以 Kaily 探需电话为准。"
                        : "All tiers include 30 days of content output + brand-color lock + optional Publer auto-publish. Final pricing is set during the discovery call with Kaily."}
                </p>
            </section>

            {/* Inquiry form */}
            <section className="max-w-3xl mx-auto px-5 sm:px-8 pb-20" id="inquiry">
                <div className="bg-cafe-cream rounded-3xl p-7 sm:p-10 border-2 border-cafe-olive/25 shadow-sm">
                    {submitted ? (
                        <div className="text-center py-10" data-testid="b2b-success">
                            <div className="w-16 h-16 rounded-full bg-cafe-olive/15 flex items-center justify-center mx-auto mb-4">
                                <Check className="h-8 w-8 text-cafe-olive" />
                            </div>
                            <h2 className="font-display text-3xl text-cafe-espresso mb-3">
                                {lang === "cn" ? "收到!" : "Got it!"}
                            </h2>
                            <p className="text-cafe-mocha text-base leading-relaxed max-w-md mx-auto">
                                {lang === "cn"
                                    ? "Kaily 会在 24 小时内通过 email 联系你,我们一起把你品牌的内容引擎跑起来。"
                                    : "Kaily will reply within 24h via email and book a discovery call so we can scope your chain's content engine together."}
                            </p>
                            <Link
                                to={homePath}
                                className="inline-block mt-7 text-cafe-olive underline text-sm font-semibold"
                                data-testid="b2b-return-home"
                            >
                                {lang === "cn" ? "← 返回首页" : "← Back to home"}
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-5" data-testid="b2b-inquiry-form">
                            <div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-cafe-olive font-bold mb-1.5">
                                    {lang === "cn" ? "已选:" : "Selected:"}{" "}
                                    <span className="text-cafe-espresso">
                                        {TIERS.find((t) => t.slug === selectedTier)?.[lang === "cn" ? "outlets_label_cn" : "outlets_label_en"]}
                                    </span>
                                </p>
                                <h2 className="font-display text-3xl sm:text-4xl text-cafe-espresso leading-tight">
                                    {lang === "cn" ? "聊聊你的连锁。" : "Tell us about your chain."}
                                </h2>
                                <p className="text-cafe-mocha text-sm mt-2">
                                    {lang === "cn"
                                        ? "30 秒填完。Kaily 看到后会主动联系你 — 不发模板,不群发。"
                                        : "30 seconds to fill. Kaily reads every inquiry herself — no auto-responder, no list-spam."}
                                </p>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="company_name" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                        {lang === "cn" ? "公司 / 品牌名" : "Company / brand"} *
                                    </Label>
                                    <Input
                                        id="company_name"
                                        data-testid="b2b-company-input"
                                        value={form.company_name}
                                        onChange={update("company_name")}
                                        required
                                        placeholder={lang === "cn" ? "如:Mama Lim 茶饮集团" : "e.g. Mama Lim Tea Co."}
                                        className="bg-white border-cafe-olive/30"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="contact_name" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                        {lang === "cn" ? "你的名字" : "Your name"} *
                                    </Label>
                                    <Input
                                        id="contact_name"
                                        data-testid="b2b-contact-input"
                                        value={form.contact_name}
                                        onChange={update("contact_name")}
                                        required
                                        placeholder={lang === "cn" ? "如:Jamie Tan" : "e.g. Jamie Tan"}
                                        className="bg-white border-cafe-olive/30"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="email" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                        <Mail className="inline h-3 w-3 mr-1" /> Email *
                                    </Label>
                                    <Input
                                        id="email"
                                        data-testid="b2b-email-input"
                                        type="email"
                                        value={form.email}
                                        onChange={update("email")}
                                        required
                                        placeholder="hello@brand.my"
                                        className="bg-white border-cafe-olive/30"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="phone" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                        <Phone className="inline h-3 w-3 mr-1" /> {lang === "cn" ? "电话 (可选)" : "Phone (optional)"}
                                    </Label>
                                    <Input
                                        id="phone"
                                        data-testid="b2b-phone-input"
                                        value={form.phone}
                                        onChange={update("phone")}
                                        placeholder="+60 12 345 6789"
                                        className="bg-white border-cafe-olive/30"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <Label htmlFor="locations" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                        <Users className="inline h-3 w-3 mr-1" /> {lang === "cn" ? "门店数量" : "Number of outlets"} *
                                    </Label>
                                    <Input
                                        id="locations"
                                        data-testid="b2b-locations-input"
                                        type="number"
                                        min={2}
                                        max={200}
                                        value={form.locations}
                                        onChange={update("locations")}
                                        required
                                        placeholder="10"
                                        className="bg-white border-cafe-olive/30"
                                    />
                                </div>
                                <div>
                                    <Label htmlFor="monthly_budget" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                        {lang === "cn" ? "月度预算 (可选)" : "Monthly budget (optional)"}
                                    </Label>
                                    <Input
                                        id="monthly_budget"
                                        data-testid="b2b-budget-input"
                                        value={form.monthly_budget}
                                        onChange={update("monthly_budget")}
                                        placeholder={lang === "cn" ? "如:RM 2,000-3,000/月" : "e.g. RM 2,000-3,000/mo"}
                                        className="bg-white border-cafe-olive/30"
                                    />
                                </div>
                            </div>

                            <div>
                                <Label htmlFor="notes" className="text-cafe-espresso text-xs font-semibold mb-1.5 block">
                                    {lang === "cn" ? "其他想聊的 (可选)" : "Anything else (optional)"}
                                </Label>
                                <Textarea
                                    id="notes"
                                    data-testid="b2b-notes-input"
                                    value={form.notes}
                                    onChange={update("notes")}
                                    placeholder={lang === "cn"
                                        ? "如:已有品牌色系想保留 / 想 Q3 全国铺开 / 跟现有摄影师合作不顺等"
                                        : "e.g. We have existing brand colours to preserve / planning Q3 nationwide rollout / current agency relationship not working"}
                                    rows={4}
                                    className="bg-white border-cafe-olive/30"
                                />
                            </div>

                            <Button
                                type="submit"
                                disabled={submitting}
                                data-testid="b2b-submit-btn"
                                className="w-full sm:w-auto px-8 py-6 rounded-full bg-cafe-olive hover:bg-cafe-oliveDark text-cafe-cream text-sm font-bold uppercase tracking-[0.14em] disabled:opacity-60"
                            >
                                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                                {lang === "cn" ? "提交咨询" : "Send inquiry"}
                            </Button>

                            <p className="text-[11px] text-cafe-mocha/70 italic flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {lang === "cn"
                                    ? "Selangor 工作室 · 全马服务 · 24 小时内回复"
                                    : "Selangor studio · serves all Malaysia · 24h reply"}
                            </p>
                        </form>
                    )}
                </div>
            </section>
        </div>
    );
}
